import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@cibli/shared';
import { ArrowDown, ArrowUp, Gauge, Globe, RefreshCw, Wifi, WifiOff } from 'lucide-react';

import { NETWORK_THRESHOLDS, type ConnectionStatus } from '@/lib/network-speed';

import { useNetworkMonitor } from '../contexts/network-monitor-context';

const statusStyles: Record<ConnectionStatus, string> = {
  good: 'bg-green-500/15 text-green-700 dark:text-green-400',
  degraded: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  critical: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

const statusLabels: Record<ConnectionStatus, string> = {
  good: 'Bonne',
  degraded: 'Dégradée',
  critical: 'Critique',
};

// Plain-language verdicts: what a non-technical operator needs to know and do.
// The degraded verdict depends on the cause: symptoms (latency/jitter/loss)
// affect the voice now; speeds alone only signal a non-conforming link.
const verdicts = {
  good: 'La connexion est bonne. Les entretiens peuvent se dérouler normalement.',
  degradedSymptomatic:
    'Connexion affaiblie : la voix peut se couper par moments. Surveillez et relancez un test dans quelques minutes.',
  degradedConformance:
    "La borne n'est plus sur le réseau prévu. Les entretiens fonctionnent — signalez-le au support.",
  critical:
    'Connexion mauvaise : les entretiens risquent de ne pas fonctionner. Vérifiez le réseau (box, câble, WiFi) ou contactez le support.',
} as const;

type MetricStatus = ConnectionStatus | 'unknown';

const dotStyles: Record<MetricStatus, string> = {
  good: 'bg-green-500',
  degraded: 'bg-amber-500',
  critical: 'bg-red-500',
  unknown: 'bg-gray-300',
};

// "upper" = worse when high (ms, %), "lower" = worse when low (Mbps)
function rateUpper(value: number | null, t: { degraded: number; critical: number }): MetricStatus {
  if (value === null) return 'unknown';
  if (value > t.critical) return 'critical';
  if (value > t.degraded) return 'degraded';
  return 'good';
}

function rateLower(value: number | null, t: { degraded: number; critical: number }): MetricStatus {
  if (value === null) return 'unknown';
  if (value < t.critical) return 'critical';
  if (value < t.degraded) return 'degraded';
  return 'good';
}

const STATUS_RANK: Record<MetricStatus, number> = {
  unknown: -1,
  good: 0,
  degraded: 1,
  critical: 2,
};

function worst(...statuses: MetricStatus[]): ConnectionStatus {
  const best = statuses.reduce<MetricStatus>(
    (a, b) => (STATUS_RANK[b] > STATUS_RANK[a] ? b : a),
    'good',
  );
  return best === 'unknown' ? 'good' : best;
}

interface MetricRowProps {
  label: string;
  hint: string;
  value: string;
  status: MetricStatus;
}

function MetricRow({ label, hint, value, status }: MetricRowProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-start gap-2">
        <span
          className={`mt-1.5 size-2.5 shrink-0 rounded-full ${dotStyles[status]}`}
          aria-hidden="true"
        />
        <span>
          {label}
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
      </span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

export function NetworkSpeedCard() {
  const {
    online,
    quality,
    lastFullTest,
    lastInternetTest,
    isFullTestRunning,
    fullTestProgress,
    testStepLabel,
    runManualTest,
  } = useNetworkMonitor();

  // Compute overall status from current metrics (same as individual dots)
  // so the badge stays in sync with the displayed numbers.
  const overallStatus: ConnectionStatus = worst(
    rateUpper(quality.latencyAvgMs, NETWORK_THRESHOLDS.latencyMs),
    rateUpper(quality.jitterMs, NETWORK_THRESHOLDS.jitterMs),
    rateUpper(quality.lossPercent, NETWORK_THRESHOLDS.lossPercent),
    rateLower(lastFullTest?.downloadMbps ?? null, NETWORK_THRESHOLDS.downloadMbps),
    rateLower(lastFullTest?.uploadMbps ?? null, NETWORK_THRESHOLDS.uploadMbps),
  );

  const symptomatic =
    rateUpper(quality.latencyAvgMs, NETWORK_THRESHOLDS.latencyMs) !== 'good' ||
    rateUpper(quality.jitterMs, NETWORK_THRESHOLDS.jitterMs) !== 'good' ||
    rateUpper(quality.lossPercent, NETWORK_THRESHOLDS.lossPercent) !== 'good';

  const verdict =
    overallStatus === 'good'
      ? verdicts.good
      : overallStatus === 'critical'
        ? verdicts.critical
        : symptomatic
          ? verdicts.degradedSymptomatic
          : verdicts.degradedConformance;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Gauge className="w-5 h-5" />
            Connexion réseau
          </span>
          <Badge variant="outline" className={statusStyles[overallStatus]}>
            {online ? <Wifi className="size-3" /> : <WifiOff className="size-3" />}
            {statusLabels[overallStatus]}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm">{verdict}</p>

        <MetricRow
          label="Réactivité"
          hint={`temps de réponse — limite : ${NETWORK_THRESHOLDS.latencyMs.degraded} ms`}
          value={quality.latencyAvgMs === null ? '—' : `${Math.round(quality.latencyAvgMs)} ms`}
          status={rateUpper(quality.latencyAvgMs, NETWORK_THRESHOLDS.latencyMs)}
        />
        <MetricRow
          label="Fluidité de la voix"
          hint={`au-delà de ${NETWORK_THRESHOLDS.jitterMs.degraded} ms, la voix devient saccadée`}
          value={quality.jitterMs === null ? '—' : `${Math.round(quality.jitterMs)} ms`}
          status={rateUpper(quality.jitterMs, NETWORK_THRESHOLDS.jitterMs)}
        />
        <MetricRow
          label="Coupures réseau"
          hint={`au-delà de ${NETWORK_THRESHOLDS.lossPercent.degraded} %, la borne perd par moments le contact avec le serveur`}
          value={`${quality.lossPercent.toFixed(0)} %`}
          status={rateUpper(quality.lossPercent, NETWORK_THRESHOLDS.lossPercent)}
        />
        <MetricRow
          label="Vitesse de réception"
          hint={`en dessous de ${NETWORK_THRESHOLDS.downloadMbps.degraded} Mbps, la borne n'est plus sur le réseau prévu (fibre/5G)`}
          value={lastFullTest ? `${lastFullTest.downloadMbps.toFixed(1)} Mbps` : '—'}
          status={rateLower(lastFullTest?.downloadMbps ?? null, NETWORK_THRESHOLDS.downloadMbps)}
        />
        <MetricRow
          label="Vitesse d'envoi (micro)"
          hint={`en dessous de ${NETWORK_THRESHOLDS.uploadMbps.degraded} Mbps, la borne n'est plus sur le réseau prévu (fibre/5G)`}
          value={lastFullTest ? `${lastFullTest.uploadMbps?.toFixed(1) ?? '—'} Mbps` : '—'}
          status={rateLower(lastFullTest?.uploadMbps ?? null, NETWORK_THRESHOLDS.uploadMbps)}
        />

        {/* ── Internet test (Cloudflare) ── */}
        <div className="flex flex-col gap-1 rounded-lg border p-3">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <Globe className="size-4" />
            Vitesse Internet
          </span>
          <span className="text-xs text-muted-foreground italic">
            Débit réel vers Internet — méthodologie Cloudflare, comparable à un test Ookla ou
            Google.
          </span>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-sm">
              <ArrowDown className="size-4" />
              Descendant
            </span>
            <span className="font-mono">
              {lastInternetTest?.downloadMbps != null
                ? `${lastInternetTest.downloadMbps.toFixed(1)} Mbps`
                : '—'}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-sm">
              <ArrowUp className="size-4" />
              Montant
            </span>
            <span className="font-mono">
              {lastInternetTest?.uploadMbps != null
                ? `${lastInternetTest.uploadMbps.toFixed(1)} Mbps`
                : '—'}
            </span>
          </div>
        </div>

        {isFullTestRunning && (
          <div className="flex flex-col gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-primary">
                <RefreshCw className="size-3.5 animate-spin" />
                {testStepLabel || 'Test de la connexion réseau en cours…'}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {Math.round(fullTestProgress * 100)} %
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${Math.round(fullTestProgress * 100)}%` }}
              />
            </div>
          </div>
        )}

        <Button
          onClick={runManualTest}
          disabled={isFullTestRunning}
          variant="outline"
          className="h-12 w-full font-medium"
        >
          <RefreshCw className={`size-4 ${isFullTestRunning ? 'animate-spin' : ''}`} />
          {isFullTestRunning
            ? testStepLabel
              ? testStepLabel
              : 'Test en cours…'
            : 'Lancer le test'}
        </Button>
      </CardContent>
    </Card>
  );
}
